<template>
    <div class="phone-input" :class="[variant, { open, error }]" v-click-outside="close">
        <button type="button" class="phone-input__country" @click="open = !open">
            <span class="phone-input__flag">{{ current.flag }}</span>
            <span class="phone-input__arrow" :class="{ open }">▾</span>
        </button>

        <input ref="inputRef" type="tel" class="phone-input__field" :placeholder="placeholder" autocomplete="tel" />

        <div v-if="open" class="phone-input__list" @mousedown.prevent>
            <input v-model="search" class="phone-input__search" :placeholder="searchPlaceholder" ref="searchRef" />
            <ul class="phone-input__items">
                <li v-for="c in filtered" :key="c.iso2" class="phone-input__item"
                    :class="{ active: c.iso2 === current.iso2 }" @click="selectCountry(c)">
                    <span class="phone-input__flag">{{ c.flag }}</span>
                    <span class="phone-input__name">{{ c.name }}</span>
                    <span class="phone-input__code">{{ c.dial }}</span>
                </li>
                <li v-if="!filtered.length" class="phone-input__empty">—</li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import IMask from 'imask'
import { getCountries, getCountryCallingCode, getExampleNumber } from 'libphonenumber-js'
import examples from 'libphonenumber-js/examples.mobile.json'

const { locale } = useI18n()

const props = defineProps({
    modelValue: { type: String, default: '' },
    defaultCountry: { type: String, default: 'UZ' },
    variant: { type: String, default: '' },
    error: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search' },
    priorityCountries: {
        type: Array,
        default: () => ['UZ', 'RU', 'KZ', 'KG', 'TJ', 'TM', 'AZ', 'BY', 'UA', 'GE', 'AM', 'TR', 'US', 'GB', 'DE', 'CN'],
    },
})
const emit = defineEmits(['update:modelValue'])

function iso2ToFlag(iso2) {
    if (!iso2 || iso2.length !== 2) return ''
    const base = 0x1F1E6 - 'A'.charCodeAt(0)
    return String.fromCodePoint(base + iso2.charCodeAt(0)) + String.fromCodePoint(base + iso2.charCodeAt(1))
}

function maskFromExample(iso2) {
    try {
        const ex = getExampleNumber(iso2, examples)
        if (!ex) return null
        const intl = ex.formatInternational()
        const dial = getCountryCallingCode(iso2)
        const rest = intl.slice(('+' + dial).length).replace(/\d/g, '0')
        return `+{${dial}}${rest}`
    } catch (e) {
        return null
    }
}

const countryNames = computed(() => {
    try {
        return new Intl.DisplayNames([locale.value || 'en'], { type: 'region' })
    } catch (e) {
        return new Intl.DisplayNames(['en'], { type: 'region' })
    }
})

function nameFor(iso2) {
    try {
        return countryNames.value.of(iso2) || iso2
    } catch (e) {
        return iso2
    }
}

const countries = computed(() => {
    const all = getCountries().map(iso2 => {
        let dial = ''
        try { dial = getCountryCallingCode(iso2) } catch (e) { dial = '' }
        if (!dial) return null
        const mask = maskFromExample(iso2) || `+{${dial}} 000000000000`
        return {
            iso2,
            name: nameFor(iso2),
            flag: iso2ToFlag(iso2),
            dial: '+' + dial,
            mask,
        }
    }).filter(Boolean)

    const priority = props.priorityCountries
    const prioritySet = new Set(priority)
    const head = priority
        .map(iso2 => all.find(c => c.iso2 === iso2))
        .filter(Boolean)
    const tail = all
        .filter(c => !prioritySet.has(c.iso2))
        .sort((a, b) => a.name.localeCompare(b.name))
    return [...head, ...tail]
})

const open = ref(false)
const search = ref('')
const inputRef = ref(null)
const searchRef = ref(null)
const current = ref(
    countries.value.find(c => c.iso2 === props.defaultCountry) || countries.value[0]
)

const filtered = computed(() => {
    const q = search.value.trim().toLowerCase()
    const list = countries.value
    if (!q) return list
    return list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.dial.includes(q)
    )
})

const placeholder = computed(() => current.value.mask.replace(/[{}]/g, '').replace(/0/g, '_'))

let mask = null

function initMask() {
    if (!inputRef.value) return
    if (mask) {
        mask.destroy()
        mask = null
    }
    mask = IMask(inputRef.value, {
        mask: current.value.mask,
        lazy: false,
        eager: true,
        placeholderChar: '_',
        overwrite: 'shift',
    })
    mask.on('accept', () => {
        emit('update:modelValue', mask.value || '')
    })
    if (props.modelValue) {
        mask.value = props.modelValue
    }
}

function selectCountry(c) {
    current.value = c
    open.value = false
    search.value = ''
    nextTick(() => {
        if (mask) {
            mask.updateOptions({ mask: c.mask })
            mask.value = ''
        }
        inputRef.value?.focus()
    })
}

function close() {
    open.value = false
    search.value = ''
}

watch(() => props.modelValue, (v) => {
    if (mask && v !== mask.value) {
        mask.value = v || ''
    }
})

watch(open, (v) => {
    if (v) nextTick(() => searchRef.value?.focus())
})

onMounted(() => {
    initMask()
})

onBeforeUnmount(() => {
    if (mask) {
        mask.destroy()
        mask = null
    }
})

const vClickOutside = {
    mounted(el, binding) {
        el.__clickOutside = (e) => {
            if (!el.contains(e.target)) binding.value(e)
        }
        document.addEventListener('click', el.__clickOutside)
    },
    unmounted(el) {
        document.removeEventListener('click', el.__clickOutside)
    },
}
</script>

<style scoped>
.phone-input {
    position: relative;
    display: flex;
    align-items: stretch;
    border: 1px solid var(--secondary-dark);
    background-color: var(--secondary-dark);
    border-radius: 12px;
    overflow: visible;
    transition: border-color 0.15s linear;
}

.phone-input.light {
    border-color: var(--primary-inverse);
    background-color: var(--primary-inverse);
}

.phone-input.secondary {
    border-color: var(--tertiary);
    background-color: var(--tertiary);
}

.phone-input.error {
    border-color: #F45725;
}

.phone-input__country {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    background: transparent;
    border: none;
    border-right: 1px solid rgba(255, 255, 255, 0.12);
    cursor: pointer;
    color: var(--primary-inverse);
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    outline: none;
}

.phone-input__country:hover {
    background-color: rgba(255, 255, 255, 0.06);
}

.phone-input.light .phone-input__country,
.phone-input.secondary .phone-input__country {
    color: var(--primary);
    border-right-color: rgba(0, 0, 0, 0.08);
}

.phone-input.light .phone-input__country:hover,
.phone-input.secondary .phone-input__country:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.phone-input__flag {
    font-size: 18px;
    line-height: 1;
}

.phone-input__arrow {
    font-size: 10px;
    opacity: 0.6;
    transition: transform 0.15s linear;
}

.phone-input__arrow.open {
    transform: rotate(180deg);
}

.phone-input__field {
    flex: 1;
    min-width: 0;
    padding: 14px 14px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    color: var(--primary-inverse);
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    font-feature-settings: "tnum";
}

.phone-input__field::placeholder {
    color: var(--primary-inverse);
    opacity: 0.45;
    letter-spacing: 0.5px;
}

.phone-input.light .phone-input__field,
.phone-input.secondary .phone-input__field {
    color: var(--primary);
}

.phone-input.light .phone-input__field::placeholder,
.phone-input.secondary .phone-input__field::placeholder {
    color: var(--primary);
}

.phone-input__list {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    min-width: 320px;
    max-width: 100%;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    z-index: 50;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.phone-input__search {
    margin: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    outline: none;
    font-size: 14px;
    color: var(--primary);
}

.phone-input__items {
    list-style: none;
    margin: 0;
    padding: 0 0 6px;
    max-height: 260px;
    overflow-y: auto;
}

.phone-input__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 15px;
    color: var(--primary);
}

.phone-input__item:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.phone-input__item.active {
    background-color: rgba(0, 0, 0, 0.06);
    font-weight: 600;
}

.phone-input__name {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.phone-input__code {
    opacity: 0.6;
}

.phone-input__empty {
    padding: 16px;
    text-align: center;
    opacity: 0.6;
}
</style>
